// janus-engine/movement/move-destination.ts — Janus session 3 (Movement) step 2, 2026-09-29: the FORCED-MOVE destination resolver.
//
// Twin of ft_resolve_move_destination($encounter, $row, x, z, y, ft3d_walk: true, free_elevation) — the call move_target() makes for every
// push / pull; the current destination resolver is below — on a depth-authored encounter:
//   1. the movement shape: the floor band under the mover's CURRENT feet (a walk snap) or under the proposed point (free elevation, its z
//      first raised to the origin band's floor), plus the bounded open ground an exterior band / open canvas adds
//      (floorBandShape / withExteriorOpenGround — lift-occupancy.ts, oracle-tested against the same PHP helpers);
//   2. the support z: a walk snap stands on the floor of the box containing the proposed point nearest the current feet (ties: the higher
//      floor), roof decks included for a mover already on one (ft3d_small_elevation_support's base support — inventory / corpse tops are the
//      CALLER's hand-back); free elevation keeps its z;
//   3. ft_resolve_free_position(): the nearest LEGAL spot (ft3d-move-core.js resolvePlacement — the golden twin of
//      ft_move_resolve_placement) for the body's support-plane footprint, the obstacle set the caller built (ft_encounter_occupancy_obstacles
//      minus the mover's compound), a mesh body's navigation shape, headroom = the body height, the ray at the body's middle.
// Answers `placed` (maybe NUDGED), `blocked` (PHP leaves the row where it was — move_target() returns before any write) or `unknown`
// (the page's shape lacks a runtime overlay the answer would read → the caller hands back).
// Oracle: tools/probes/movement_destination_oracle.php → tools/test/fixtures/movement-destination-oracle-*.json
//         (tools/test/janus-movement-destination.test.mjs).
import { containingBoxes } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { resolvePlacement } from '../../../../ft3d/engine/ft3d-move-core.js';
import { floorBandShape, withExteriorOpenGround } from '../inventory/lift-occupancy.js';
import { withRoofDecks, type RestShape, type Zone } from '../inventory/floor-move.js';
import { physicalBodyNavigationShape } from '../../shared/physical-creature-body.js';
import { sceneWallNearBody } from './scene-wall-proximity.js';
import { movementFootprint } from './body-geometry.js';

export type Point = { x: number; y: number; z: number };
export interface DestinationBody {
  widthFt?: number;
  /** ft_ground_collision_diameter_ft(): the mesh body's width, else the size tier. */
  diameter: number;
  /** ft_ground_occupancy_height_ft(): the mesh body's height, else the size tier. */
  height: number;
  /** ft_ground_occupancy_base_size(): the size tier (the open-ground margin). */
  baseFt: number;
  /** A banked physical mesh body (ft_creature_physical_mesh_bounds) — its too-short openings close. */
  mesh: boolean;
  squeezeSizeFt?: number;
}
export interface MoveDestinationInput {
  shape: RestShape;
  /** ft_encounter_canvas_is_exterior(): encounter_inside !== 1. */
  canvasExterior: boolean;
  /** The page's shape may lack live door / breach / curtain / roof overlays: an answer that reads a face near one → unknown. */
  overlayUnknown: boolean;
  /** Building roofs are a runtime overlay (ft_roof_overlay — a destroyed roof opens its rooms, and its deck stops being ground): when a roof
   *  deck is in play at the feet, the support / band the page reads may not be PHP's → unknown (Codex step-2 review F5). */
  roofsUnknown?: boolean;
  /** The mover's PERSISTED feet (pos_coord) — the walk snap's floor band and support tie-break read them. */
  persisted: Point;
  proposed: Point;
  /** move_target(): free elevation when the displacement itself changes height. */
  free: boolean;
  /** Dismount search keeps the origin floor band and elevation, excluding object-top support. */
  groundOnly?: boolean;
  /** Loose inventory and rigid callers opt out; ordinary creature movement uses the squeeze ladder. */
  allowSqueeze?: boolean;
  body: DestinationBody;
  /** ft_encounter_occupancy_obstacles($encounter, $mover, compound excluded, support_plane_z: true) — support-plane zones. */
  obstacles: Zone[];
}
export type MoveDestination = { status: 'placed'; x: number; y: number; z: number; moved: boolean }
  | { status: 'blocked'; reason: string } | { status: 'unknown'; reason: string };
const EPS = 1e-6;
type Box = RestShape['boxes'][number];

/** ft_resolve_first_unobstructed_position: nearest clear ring, deterministic distance/X/Y ties. */
export function resolveFirstUnobstructedPosition(origin: Point, baseFt: number,
  resolve: (point: Point) => MoveDestination, maximumFt = 40): MoveDestination {
  const direct = resolve(origin);
  if (direct.status === 'unknown') return direct;
  if (direct.status === 'placed') return direct.moved ? direct : { status: 'blocked', reason: 'already-clear' };
  const step = Math.max(0.5, Math.min(2.5, baseFt / 4));
  for (let radius = step; radius <= Math.max(step, maximumFt) + EPS; radius += step) {
    const samples = Math.max(8, Math.ceil(2 * Math.PI * radius / step));
    const clear: Array<Extract<MoveDestination, { status: 'placed' }> & { distance: number }> = [];
    for (let sample = 0; sample < samples; sample++) {
      const angle = 2 * Math.PI * sample / samples;
      const candidate = resolve({ x: origin.x + Math.cos(angle) * radius, y: origin.y + Math.sin(angle) * radius, z: origin.z });
      if (candidate.status === 'unknown') return candidate;
      if (candidate.status !== 'placed') continue;
      const distance = Math.hypot(candidate.x - origin.x, candidate.y - origin.y);
      if (distance >= 0.01) clear.push({ ...candidate, distance });
    }
    if (!clear.length) continue;
    clear.sort((a, b) => a.distance - b.distance || a.x - b.x || a.y - b.y);
    const { distance: _, ...result } = clear[0]!;
    return { ...result, moved: true };
  }
  return { status: 'blocked', reason: 'no-unobstructed-position' };
}

export function resolveMoveDestination(d: MoveDestinationInput): MoveDestination {
  const { body } = d;
  if (![body.diameter, body.height, body.baseFt].every(v => Number.isFinite(v) && v > 0)) return { status: 'unknown', reason: 'body' };
  let proposedZ = d.proposed.z;
  let movement: RestShape = d.shape;
  if (d.roofsUnknown && Array.isArray(d.shape.boxes)) {
    const decked = (z: number): boolean => withRoofDecks(d.shape, z).boxes.length !== d.shape.boxes.length;
    if (decked(d.persisted.z) || (d.free && decked(d.proposed.z))) return { status: 'unknown', reason: 'roof-overlay' };
  }
  if (Array.isArray(d.shape.boxes)) {
    if (d.free && !d.groundOnly) {
      const origin = floorBandShape(d.shape, d.persisted.x, d.persisted.y, d.persisted.z, d.canvasExterior);
      if (origin) proposedZ = Math.max(proposedZ, origin.floor);
    }
    const current = d.free && !d.groundOnly ? { x: d.proposed.x, y: d.proposed.y, z: proposedZ } : d.persisted;
    const band = floorBandShape(d.shape, current.x, current.y, current.z, d.canvasExterior);
    if (band) movement = withExteriorOpenGround(band.shape, current.x, current.y, d.proposed.x, d.proposed.y, 40 + body.baseFt, d.canvasExterior);
  }
  let z = proposedZ;
  if (!d.free) {
    // ft3d_small_elevation_support(): the base support — the containing box (roof decks for a mover already on one) nearest the feet.
    const currentZ = d.persisted.z;
    const boxes = (containingBoxes(withRoofDecks(movement, currentZ) as never, d.proposed.x, d.proposed.y) as Box[]).slice();
    boxes.sort((a, b) => {
      const cmp = Math.abs(Number(a.floor) - currentZ) - Math.abs(Number(b.floor) - currentZ);
      return cmp !== 0 ? cmp : Number(b.floor) - Number(a.floor);
    });
    z = boxes.length ? Number(boxes[0]!.floor) : 0;
  }
  const mover = movementFootprint(body, d.proposed.x, d.proposed.y, z);
  const placementShape = body.mesh ? physicalBodyNavigationShape(movement as never, body.height) as unknown as RestShape : movement;
  const rayZ = (z + z + body.height) / 2;
  const result = resolvePlacement(placementShape as never, mover as never, d.obstacles as never,
    { rayZ, headroomFt: body.height, allowSqueeze: d.allowSqueeze ?? !body.mesh, squeezeSizeFt: body.squeezeSizeFt }) as
    { status: string; x: number; y: number; moved?: boolean; reason?: string };
  if (d.overlayUnknown) {
    // Door states, breaches and curtains the page does not hold only matter where the answer read a face: the body's clearance at the
    // proposed and the final point, and a nudge's reachability ray.
    const reach = body.diameter / 2 + 0.5;
    const nearFace = (x: number, y: number): boolean => placementShape.boxes.some(box => !box.navigationOpenGround && rayZ >= box.floor - 0.5 && rayZ <= box.ceiling + 0.5
      && ([box.x.min, box.x.max].some(at => Math.abs(x - at) < reach) || [box.y.min, box.y.max].some(at => Math.abs(y - at) < reach)));
    // a nudge SEARCH (successful or wedged) reads candidate clearances and reachability rays through faces whose door state the page lacks
    // (Codex step-2 review F4)
    const searched = (result.status === 'placed' && result.moved) || (result.status !== 'placed' && (result.reason === 'wedged' || result.reason === 'occupied'));
    if (nearFace(d.proposed.x, d.proposed.y) || (result.status === 'placed' && nearFace(result.x, result.y))
      || (searched && (placementShape.openings?.length ?? 0) > 0)
      || (Array.isArray(placementShape.sceneWalls) && placementShape.sceneWalls.some(wall => sceneWallNearBody(wall, { ...d.proposed, z }, body.diameter / 2, body.height)
        || (result.status === 'placed' && sceneWallNearBody(wall, { x: result.x, y: result.y, z }, body.diameter / 2, body.height)))))
      return { status: 'unknown', reason: 'overlay' };
  }
  if (result.status !== 'placed') return { status: 'blocked', reason: String(result.reason ?? 'blocked') };
  return { status: 'placed', x: result.x, y: result.y, z, moved: result.moved === true
    || Math.abs(result.x - d.proposed.x) > EPS || Math.abs(result.y - d.proposed.y) > EPS };
}
